import { CalendarClock } from "lucide-react";

/**
 * UCASDeadlineChip — compact chip surfacing UCAS deadlines on UK
 * undergraduate courses. Mirrors the `<CAODeadlineChip>` UX so
 * families see the cutoff at a glance on Advanced Results cards,
 * Shortlist rows and the Degree Detail application section.
 *
 * UCAS runs two headline undergraduate deadlines that never move:
 *   • 15 October  — Oxford, Cambridge + all Medicine / Dentistry /
 *                    Veterinary Science courses (regardless of uni)
 *   • 25 January  — Everything else, "equal consideration"
 *
 * The chip renders one of three variants based on the degree data:
 *   • Oxbridge / medicine → "🇬🇧 UCAS · 15 Oct {year}"
 *   • Everything else UK  → "🇬🇧 UCAS · 25 Jan {year}"
 *   • Both (dual signal)  → "🇬🇧 UCAS · 15 Oct / 25 Jan {year}"
 *
 * Renders `null` for non-UK / non-undergraduate rows so it's safe
 * to drop into any card template unconditionally.
 */

const _MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const _OXBRIDGE_UNI_IDS = new Set(["uk-oxford", "uk-cambridge"]);

// Subject-area strings that trigger the 15 Oct early deadline
// regardless of uni. Loose contains-match on lowercase.
const _EARLY_DEADLINE_SUBJECTS = [
  "medicine", "dentistry", "veterinary",
];


function _isUKUndergrad(degree) {
  if (!degree) return false;
  const explicit = (degree.country || "").trim().toLowerCase();
  const uidPrefix = (degree.university_id || "").split("-")[0].toLowerCase();
  const isUK = explicit === "uk" || explicit === "united kingdom"
    || explicit === "britain" || (!explicit && uidPrefix === "uk");
  if (!isUK) return false;
  // Skip masters/postgrad — they have separate deadlines per uni.
  const level = (degree.level || "").toLowerCase();
  if (level && level !== "undergraduate" && level !== "ug") return false;
  return true;
}


function _needsEarlyDeadline(degree) {
  if (!degree) return false;
  if (_OXBRIDGE_UNI_IDS.has(degree.university_id)) return true;
  const subj = (degree.subject_area || "").toLowerCase();
  return _EARLY_DEADLINE_SUBJECTS.some((k) => subj.includes(k));
}


function _cycleYear(degree) {
  // UCAS applications close in the CALENDAR year BEFORE the intake
  // year. e.g. a Sept-2027 intake has 15 Oct 2026 + 25 Jan 2027
  // deadlines (Oct-Jan straddles the calendar-year boundary).
  const intake = (degree?.intake || "").toString();
  const m = intake.match(/^(\d{4})/);
  if (!m) return null;
  const intakeYear = parseInt(m[1], 10);
  return { earlyYear: intakeYear - 1, mainYear: intakeYear };
}


export function UCASDeadlineChip({ degree, testid, now = new Date() }) {
  if (!_isUKUndergrad(degree)) return null;
  const cycle = _cycleYear(degree);
  if (!cycle) return null;
  const { earlyYear, mainYear } = cycle;
  const isEarly = _needsEarlyDeadline(degree);

  // Post-25-Jan pivot: once the main-cycle UCAS deadline has passed,
  // the next thing families actually need to see is the enrolment-
  // deposit cutoff (per uni). Prefer a per-uni deposit_deadline
  // stored on the degree (application.deposit_deadline) before
  // falling back to the shared "UCAS Reply · 8 May" landmark.
  const past25Jan = _hasPassedMainDeadline(now, mainYear);
  if (past25Jan && !isEarly) {
    const depositIso =
      degree?.application?.deposit_deadline ||
      degree?.deposit_deadline ||
      null;
    return _renderDepositChip({ testid, mainYear, depositIso });
  }

  const label = isEarly
    ? `UCAS · 15 Oct ${earlyYear}`
    : `UCAS · 25 Jan ${mainYear}`;
  const tooltip = isEarly
    ? `Oxford/Cambridge and Medicine/Dentistry/Vet Med close 15 Oct ${earlyYear} · Everything else closes 25 Jan ${mainYear}`
    : `UCAS "equal consideration" deadline for main-cycle courses`;

  const tint = isEarly
    ? "bg-rose-50 border-rose-200 text-rose-900"
    : "bg-[#F5F0FF] border-[#4C1D95]/25 text-[#4C1D95]";

  return (
    <span
      data-testid={testid || "ucas-deadline-chip"}
      className={`inline-flex items-center gap-1.5 rounded-full border ${tint} text-[11px] font-medium px-2.5 py-1`}
      title={tooltip}
    >
      <span aria-hidden>🇬🇧</span>
      <CalendarClock className="w-3 h-3" aria-hidden />
      <span>{label}</span>
    </span>
  );
}


function _hasPassedMainDeadline(now, mainYear) {
  // 25 Jan of mainYear at 18:00 UK (roughly UTC).
  const cutoff = new Date(mainYear, 0, 25, 18, 0, 0);
  return now.getTime() > cutoff.getTime();
}


function _renderDepositChip({ testid, mainYear, depositIso }) {
  // Per-uni deposit deadline (when the degree carries one) beats
  // the shared 8 May Reply-deadline landmark — families care about
  // the ACTUAL date they'll lose their place, not the industry-wide
  // reply cutoff. Falls back to 8 May when Mongo has no per-uni row.
  const parsed = _parseISODate(depositIso);
  const label = parsed
    ? `Deposit due · ${parsed.day} ${parsed.monthShort} ${parsed.year}`
    : `UCAS Reply · 8 May ${mainYear}`;
  const tooltip = parsed
    ? `This uni's enrolment deposit is due ${parsed.day} ${parsed.monthShort} ${parsed.year}. Miss it and the place is released to a waitlist candidate.`
    : `Reply deadline for Firm + Insurance choices · 8 May ${mainYear} 18:00. Enrolment-deposit due dates follow per uni after acceptance.`;
  return (
    <span
      data-testid={testid || (parsed ? "ucas-deposit-chip-dynamic" : "ucas-deposit-chip")}
      className="inline-flex items-center gap-1.5 rounded-full border bg-amber-50 border-amber-200 text-amber-900 text-[11px] font-medium px-2.5 py-1"
      title={tooltip}
    >
      <span aria-hidden>🇬🇧</span>
      <CalendarClock className="w-3 h-3" aria-hidden />
      <span>{label}</span>
    </span>
  );
}


function _parseISODate(iso) {
  if (!iso || typeof iso !== "string") return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const [_, y, mm, dd] = m;
  const monthIdx = parseInt(mm, 10) - 1;
  if (monthIdx < 0 || monthIdx > 11) return null;
  return {
    day: parseInt(dd, 10),
    monthShort: _MONTHS_SHORT[monthIdx],
    year: parseInt(y, 10),
  };
}


export default UCASDeadlineChip;


// Debug helper — exported so pytest-lite can smoke-test the picker.
export const _internals = { _isUKUndergrad, _needsEarlyDeadline, _cycleYear };
